/**
 * Responsive layout component for the Equipment Status Overview page.
 * This component provides a responsive grid layout with breakpoints for mobile, tablet, and desktop.
 * It also includes loading and error states for the main content.
 */

import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Responsive from '@amzn/meridian/responsive';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';

export interface ResponsiveLayoutProps {
  children: React.ReactNode;
  title?: string;
  isLoading?: boolean;
  error?: Error | string | null;
  onRetry?: () => void;
}

export const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({
  children,
  title,
  isLoading = false,
  error = null,
  onRetry,
}) => {
  // Convert error to string if it's an Error object
  const errorMessage = error instanceof Error ? error.message : error;

  return (
      <Column 
        width="100%" 
        height="fill"
        backgroundColor="default"
      >
        <Responsive
          query="min-width"
          props={{
            spacing: { default: '300', '768px': '400', '1024px': '500', '1440px': '600' },
            width: { default: '95%', '768px': '96%', '1024px': '96%', '1440px': '94%' },
            maxWidth: { default: '100%', '768px': '100%', '1024px': '1800px', '1440px': '2000px', '1920px': '2200px' },
            horizontalPadding: { default: '300', '768px': '400', '1024px': '200', '1440px': '300' },
          }}
        >
          {({ spacing, width, maxWidth, horizontalPadding }) => (
            <Column 
              width={width} 
              maxWidth={maxWidth}
              spacing={spacing}
              alignmentHorizontal="center"
              spacingInset={`${spacing} ${horizontalPadding} 600`}
            >
              {/* Title if provided */}
              {title && (
                <Row>
                  <Text type="h100">{title}</Text>
                </Row>
              )}

              {/* Loading State */}
              {isLoading && (
                <Column alignmentHorizontal="center" spacing="500" spacingInset="500">
                  <Loader />
                  <Text>Loading content...</Text>
                </Column>
              )}

              {/* Error State */}
              {errorMessage && (
                <Alert
                  type="error"
                  title="Error loading content"
                >
                  {errorMessage}
                </Alert>
              )}
              {errorMessage && onRetry && (
                <Button onClick={onRetry}>Retry</Button>
              )}

              {/* Main Content - Only show if not loading and no error */}
              {!isLoading && !errorMessage && children}
            </Column>
          )}
        </Responsive>
      </Column>
  );
};
